import React, { useState, useEffect } from "react";
import {
    CalendarIcon,
    CheckIcon,
    CogIcon,
    DownloadIcon,
    SearchIcon,
    XCircleIcon,
} from "@heroicons/react/solid";
import {
    Form,
    Button,
    InputGroup,
    Dropdown,
    Nav,
    Card,
    Table,
    Pagination,
} from "react-bootstrap";
import {
    useDnldFailoverReportMutation,
    useFailoverReportsMutation,
} from "Services/services";
import moment from "moment-timezone";
import Datetime from "react-datetime";
import hasPermission from "pages/Permissions";
import { downloadBlobFile } from "components/Utility";
import Select from "react-select";


export default () => {
    // Get the user type from localStorage :: UI
    const userData = localStorage.getItem("TOKEN");
    const userObject = JSON.parse(userData);
    const userID = userObject.id;

    const [tableData, setTableData] = useState([]);

    const [searchValue, setSearchValue] = useState("");
    const [fallChannel, setFallChannel] = useState(null);
    const [fallStatus, setFallStatus] = useState(null);

    const [start, setStart] = useState(null);
    const [end, setEnd] = useState(null);

    const [pagelimit, setPagelimit] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalPosts, setTotalPosts] = useState(0);
    const [debouncedValue, setDebouncedValue] = useState("");

    const [reports] = useFailoverReportsMutation();
    const [download] = useDnldFailoverReportMutation();

    const channelOptions = [
        { label: "SMS", value: "SMS" },
        { label: "WhatsApp", value: "WhatsApp" },
        { label: "RCS", value: "RCS" }
    ]

    const statusOptions = [
        { label: "Completed", value: "completed" },
        { label: "Pending", value: "pending" },
        { label: "In-Process", value: "inprocess" }
    ]

    // Debounce search value
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(searchValue); // Set debounced value after 3 seconds
        }, 2000);
        // Cleanup the timeout if searchValue changes (to avoid multiple timeouts)
        return () => {
            clearTimeout(handler);
        };
    }, [searchValue]);

    const fetchReports = async () => {
        try {
            const response = await reports({
                page: currentPage,
                limit: pagelimit,
                search: searchValue,
                fallbackChannel: fallChannel?.value,
                status: fallStatus?.value,
                startDate: start ? start.format("YYYY-MM-DD") : "",
                endDate: end ? end.format("YYYY-MM-DD") : "",
            });

            if (response.data.statusCode === 200) {
                setTableData(response?.data?.data.items || []);
                setTotalPages(Math.ceil(response?.data?.data?.totalItems / pagelimit));
                setTotalPosts(response?.data?.data?.totalItems || 0);
            } else {
                setTableData([]);
                setTotalPages(0);
                setTotalPosts(0);
            }
        } catch (error) {
            alert(error.message);
            setTableData([]);
            setTotalPages(0);
            setTotalPosts(0);
        }
    };

    useEffect(() => {
        fetchReports();
        // eslint-disable-next-line
    }, [
        currentPage,
        pagelimit,
        debouncedValue,
        start,
        end,
        fallStatus,
        fallChannel,
    ]);

    const changeSearchValue = (e) => {
        setSearchValue(e.target.value);
    };

    // const downloadList = (ids) => { };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const canDownload = hasPermission("FailOver Analytics", "download");
    const handleDownload = async () => {
        try {
            const blob = await download({
                search: searchValue,
                fallbackChannel: fallChannel?.value,
                status: fallStatus?.value,
                startDate: start ? start.format("YYYY-MM-DD") : "",
                endDate: end ? end.format("YYYY-MM-DD") : "",
                loggedin_user_id: userID,
                user_type_id: userObject.usertype_id || "1",
            }).unwrap();
            downloadBlobFile(blob, "Failover_Reports.csv");
        } catch (err) {
            if (err?.status === 404) alert("No data found.");
            else alert("Download failed.");
        }
    }

    const TableRow = (props) => {
        return (
            <tr className="border-bottom">
                <td><span className="fw-normal" title={props.primaryCampaignName || "-"}>{props.primaryCampaignName || "-"}</span></td>
                <td><span className="fw-normal" title={props.primaryChannel || "-"}>{props.primaryChannel || "-"}</span></td>
                <td><span className="fw-normal" title={props.primaryTemplate || "-"}>{props.primaryTemplate || "-"}</span></td>
                <td><span className="fw-normal" title={props.fallbackCampaignName || "-"}>{props.fallbackCampaignName || "-"}</span></td>
                <td><span className="fw-normal" title={props.fallbackChannel || "-"}>{props.fallbackChannel || "-"}</span></td>
                <td><span className="fw-normal" title={props.fallbackTemplate || "-"}>{props.fallbackTemplate || "-"}</span></td>
                <td><span className="fw-normal" title={props.fallbackStatus || "-"}>{props.fallbackStatus || "-"}</span></td>
                <td><span className="fw-normal" title={props.fallbackTotalSubmitted || "0"}>{props.fallbackTotalSubmitted || "0"}</span></td>
                <td><span className="fw-normal" title={props.fallbackTotalFailure || "0"}>{props.fallbackTotalFailure || "0"}</span></td>
                <td><span className="fw-normal" title={props.fallbackTotalSuccess || "0"}>{props.fallbackTotalSuccess || "0"}</span></td>
                <td><span className="fw-normal" title={moment(props.fallbackStartDateTime).format('DD/MM/YYYY') || '-'}>{moment(props.fallbackStartDateTime).format('DD/MM/YYYY') || '-'}</span></td>
            </tr>
        )
    };

    return (
        <Card border={1}>

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <InputGroup className="d-flex flex-nowrap fmxw-500">
                    <InputGroup.Text>
                        <SearchIcon className="icon icon-xs" />
                    </InputGroup.Text>
                    <Form.Control
                        type="search"
                        placeholder="Search Primary / Fallback Campaign Name"
                        value={searchValue}
                        onChange={changeSearchValue}
                    />
                </InputGroup>
                <Form.Group id="DateRange">
                  <InputGroup className="d-flex flex-nowrap">
                    <InputGroup.Text
                      onClick={() => {
                        setStart(null);
                        setEnd(null);
                      }}
                    >
                      {start || end ? (
                        <XCircleIcon className="icon icon-xs" />
                      ) : (
                        <CalendarIcon className="icon icon-xs" />
                      )}
                    </InputGroup.Text>
                    <Datetime
                      timeFormat={false}
                      onChange={(date) => {
                        if (moment.isMoment(date)) {
                          setStart(date);
                        }
                      }}
                      closeOnSelect={true}
                      renderInput={(props, openCalendar) => (
                        <Form.Control
                          type="search"
                          placeholder="Start Date"
                          value={
                            start ? moment(start).format("DD/MM/YYYY") : ""
                          }
                          onFocus={openCalendar}
                          readOnly
                          style={{ width: "150px", borderRadius: "0" }}
                        />
                      )}
                    />
                    <Datetime
                      timeFormat={false}
                      onChange={(date) => {
                        if (moment.isMoment(date)) {
                          setEnd(date);
                        }
                      }}
                      closeOnSelect={true}
                      isValidDate={(currDate) => currDate.isAfter(start)}
                      initialViewDate={end}
                      renderInput={(props, openCalendar) => (
                        <Form.Control
                          type="search"
                          placeholder="End Date"
                          value={end ? moment(end).format("DD/MM/YYYY") : ""}
                          onFocus={openCalendar}
                          readOnly
                          style={{
                            width: "150px",
                            borderTopLeftRadius: "0",
                            borderBottomLeftRadius: "0",
                          }}
                        />
                      )}
                    />
                  </InputGroup>
                </Form.Group>

                <Select
                    options={channelOptions}
                    isClearable
                    placeholder="Select Channel"
                    className="fix-200"
                    classNamePrefix="react-select"
                    value={fallChannel}
                    onChange={(selectedOption) => { setFallChannel(selectedOption) }}
                />

                <Select
                    options={statusOptions}
                    placeholder="Select Status"
                    isClearable
                    className="fix-200"
                    classNamePrefix="react-select"
                    value={fallStatus}
                    onChange={(selectedOption) => { setFallStatus(selectedOption) }}
                />

                <div className="ms-auto d-inline-flex gap-2">
                    {canDownload && (
                        <Button
                            variant="outline-gray-300"
                            onClick={handleDownload}
                            className="p-2"
                        >
                            <DownloadIcon className="icon icon-sm" />
                        </Button>
                    )}

                    <Dropdown>
                        <Dropdown.Toggle
                            split
                            as={Button}
                            variant="outline-gray-300"
                            className="text-dark m-0 p-2"
                        >
                            <CogIcon className="icon icon-sm" />
                            <span className="visually-hidden">
                                Toggle Dropdown
                            </span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="dropdown-menu-xs dropdown-menu-end p-2">
                            <span className="small ps-3 fw-bold text-dark">
                                Rows per page
                            </span>
                            {[10, 20, 30].map((limit) => (
                                <Dropdown.Item
                                    key={limit}
                                    className={`d-flex align-items-center fw-bold ${limit === 30 ? "rounded-bottom" : ""
                                        }`}
                                    onClick={() => {
                                        setPagelimit(limit); // Update the page limit
                                        setCurrentPage(1); // Reset the page number to 1
                                    }}
                                >
                                    {limit}{" "}
                                    {pagelimit === limit && (
                                        <CheckIcon className="icon icon-xxs ms-auto" />
                                    )}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            <div className="custom-scroll-wrapper">
                <div className="scroll-container">
                    <Table responsive className="align-items-center table-flush table-outlines">
                        <thead className="thead-light">
                            <tr>
                                <th className="border-bottom">Primary Campaign Name</th>
                                <th className="border-bottom">Primary Channel</th>
                                <th className="border-bottom">Primary Template</th>
                                <th className="border-bottom">Fallback Campaign Name</th>
                                <th className="border-bottom">Fallback Channel</th>
                                <th className="border-bottom">Fallback Template</th>
                                <th className="border-bottom">Fallback Status</th>
                                <th className="border-bottom">Fallback Total Submitted</th>
                                <th className="border-bottom">Fallback Total Failure</th>
                                <th className="border-bottom">Fallback Total Success</th>
                                <th className="border-bottom" style={{ minWidth: '120px' }}>Date</th>
                            </tr>
                        </thead>
                        <tbody className="border-0">
                            {tableData.length > 0 ? (
                                tableData.map((u, index) => (
                                    <TableRow key={index + 1} index={index + 1} {...u} />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={11} className="text-center text-danger">
                                        No Data Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>

            <Card.Footer className="p-0 mt-3 border-0 d-flex flex-wrap align-items-center justify-content-between">
                <Nav>
                    <Pagination className="mb-0">
                        <Pagination.Prev
                            onClick={handlePrev}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </Pagination.Prev>
                        {currentPage >= 3 && (
                            <>
                                <Pagination.Item onClick={() => handlePageChange(1)}>
                                    1
                                </Pagination.Item>
                                <Pagination.Ellipsis />
                            </>
                        )}
                        {currentPage > 1 && (
                            <Pagination.Item
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                {currentPage - 1}
                            </Pagination.Item>
                        )}
                        <Pagination.Item active>{currentPage}</Pagination.Item>
                        {currentPage < totalPages && (
                            <Pagination.Item
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                {currentPage + 1}
                            </Pagination.Item>
                        )}
                        {currentPage < totalPages - 1 && (
                            <>
                                <Pagination.Ellipsis />
                                <Pagination.Item
                                    onClick={() => handlePageChange(totalPages)}
                                >
                                    {totalPages}
                                </Pagination.Item>
                            </>
                        )}
                        <Pagination.Next
                            onClick={handleNext}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </Pagination.Next>
                    </Pagination>
                </Nav>
                <small className="fw-normal small mt-2 mt-md-0">
                    Showing <b>{totalPosts === 0 ? totalPosts : pagelimit * (currentPage - 1) + 1}-{(pagelimit * currentPage) > totalPosts ? totalPosts : (pagelimit * currentPage)}</b> of <b>{totalPosts}</b>{" "}entries.
                </small>
            </Card.Footer>
        </Card>
    );
};
